import { Box, SxProps, Theme } from "@mui/material";
import { ReactNode, RefObject } from "react";
import Flex from "@/components/Flex";
import Text from "@/components/Text";
import { NAVBAR_HEIGHT } from "@/components/app/nav-bar";
import {
  CHART_COLORS,
  CHART_TYPOGRAPHY,
} from "@/components/app/provider/patient/chart";

export type GroupPageShellProps = {
  title: string;
  /** Buttons for the right of the header bar. */
  actions?: ReactNode;
  /** Rendered between the header bar and the scroll region, e.g. section chips. */
  subHeader?: ReactNode;
  /** Attach to read the scroll position, e.g. for a scroll spy. */
  scrollRef?: RefObject<HTMLDivElement>;
  children: ReactNode;
  contentSx?: SxProps<Theme>;
};

/**
 * Full-height frame for a group-visit screen. The page itself never scrolls —
 * only the content region does — so the header actions stay reachable.
 *
 * @author Rajkumar Rathod
 */
const GroupPageShell = ({
  title,
  actions,
  subHeader,
  scrollRef,
  children,
  contentSx,
}: GroupPageShellProps) => (
  <Box
    sx={{
      height: `calc(100svh - ${NAVBAR_HEIGHT})`,
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      backgroundColor: CHART_COLORS.surfaceDefault,
    }}
  >
    <Flex
      alignItems="center"
      justifyContent="space-between"
      gap={2}
      flexWrap="wrap"
      sx={{
        flexShrink: 0,
        backgroundColor: CHART_COLORS.surfaceWhite,
        borderBottom: `1px solid ${CHART_COLORS.borderLight}`,
        px: 2,
        py: 1.5,
      }}
    >
      <Text
        component="h1"
        {...CHART_TYPOGRAPHY.body3}
        fontWeight={CHART_TYPOGRAPHY.weight.medium}
        c={CHART_COLORS.textDarker}
      >
        {title}
      </Text>

      {!!actions && (
        <Flex alignItems="center" gap={1}>
          {actions}
        </Flex>
      )}
    </Flex>

    {subHeader}

    {/* minHeight lets this flex child shrink so the overflow is its own. */}
    <Box
      ref={scrollRef}
      sx={{
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        px: 2,
        py: 2,
        ...(contentSx as object),
      }}
    >
      {children}
    </Box>
  </Box>
);

export default GroupPageShell;
